import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import SiteNavbar from '@/components/feature/SiteNavbar';
import SiteFooter from '@/components/feature/SiteFooter';
import ScrollToTop from '@/components/feature/ScrollToTop';
import RevealWrapper from '@/pages/home/components/RevealWrapper';
import gSoftLogoDark from '@/pages/images/g-soft-logo-beyaz2.png';

const ARCHIVE_NOTES = [
  { labelKey: 'am.archive.status.dev.label', valueKey: 'am.archive.status.dev.value', icon: 'ri-close-circle-line' },
  { labelKey: 'am.archive.status.catalog.label', valueKey: 'am.archive.status.catalog.value', icon: 'ri-archive-line' },
  { labelKey: 'am.archive.status.store.label', valueKey: 'am.archive.status.store.value', icon: 'ri-store-line' },
  { labelKey: 'am.archive.status.restart.label', valueKey: 'am.archive.status.restart.value', icon: 'ri-refresh-line' },
] as const;

const TIMELINE = [
  { date: '2024', labelKey: 'am.timeline.1.label', noteKey: 'am.timeline.1.note', status: 'done' },
  { date: '2024', labelKey: 'am.timeline.2.label', noteKey: 'am.timeline.2.note', status: 'done' },
  { date: '2025', labelKey: 'am.timeline.3.label', noteKey: 'am.timeline.3.note', status: 'cancelled' },
] as const;

const CONCEPT_NOTES = [
  { titleKey: 'am.note.1.title', contentKey: 'am.note.1.content' },
  { titleKey: 'am.note.2.title', contentKey: 'am.note.2.content' },
  { titleKey: 'am.note.3.title', contentKey: 'am.note.3.content' },
] as const;

const TAGS = [
  'games.am.tag.atmosphere',
  'games.am.tag.storyDriven',
  'games.am.tag.mobile',
  'games.am.tag.archive',
] as const;

export default function AnilarMuzesiPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const NAV_LINKS = [
    { label: t('nav.home'), href: '/', isRoute: true },
    { label: t('nav.apps'), href: '/apps', isRoute: true },
    { label: t('nav.games'), href: '/games', isRoute: true },
    { label: t('nav.notify'), href: '/?contactProduct=anilar-muzesi#contact', isRoute: true },
  ];

  return (
    <div className="min-h-screen bg-[#181010]">
      <SiteNavbar links={NAV_LINKS} sectionAware={false} />

      <section className="relative min-h-screen bg-[#181010] flex flex-col overflow-hidden pt-16">
        <div
          className="absolute inset-0 pointer-events-none opacity-40"
          style={{
            backgroundImage:
              'linear-gradient(rgba(180,30,30,0.07) 1px, transparent 1px), linear-gradient(90deg, rgba(180,30,30,0.07) 1px, transparent 1px)',
            backgroundSize: '80px 80px',
          }}
        />
        <div className="absolute top-24 right-0 w-px h-64 bg-gradient-to-b from-transparent via-red-700/25 to-transparent" />
        <div className="absolute top-24 right-0 w-64 h-px bg-gradient-to-r from-transparent via-red-700/25 to-transparent" />

        <div className="flex-1 flex flex-col justify-center px-6 lg:px-14 py-20 lg:py-28">
          <RevealWrapper direction="up" duration={500}>
            <div className="flex items-center gap-3 mb-10">
              <button
                onClick={() => navigate('/games')}
                className="font-mono text-xs text-neutral-500 hover:text-neutral-300 tracking-widest uppercase transition-colors duration-200 flex items-center gap-2 cursor-pointer"
              >
                <i className="ri-arrow-left-line" />
                {t('product.back')}
              </button>
              <span className="text-neutral-700">/</span>
              <button
                onClick={() => navigate('/games')}
                className="font-mono text-xs text-neutral-500 hover:text-neutral-300 tracking-widest uppercase transition-colors duration-200 cursor-pointer"
              >
                {t('nav.games')}
              </button>
              <span className="text-neutral-700">/</span>
              <span className="font-mono text-xs text-red-600/70 tracking-widest uppercase">{t('am.breadcrumb.archive')}</span>
              <span className="text-neutral-700">/</span>
              <span className="font-mono text-xs text-neutral-500 tracking-widest uppercase line-through">{t('am.name')}</span>
            </div>
          </RevealWrapper>

          <RevealWrapper direction="up" delay={100} duration={500}>
            <div className="border border-red-800/40 bg-red-900/10 px-5 py-3 flex items-center gap-3 mb-12 max-w-2xl">
              <i className="ri-error-warning-line text-red-500/70 text-base" />
              <p className="font-mono text-xs text-red-400/70 tracking-wider">{t('am.archive.banner')}</p>
            </div>
          </RevealWrapper>

          <div className="grid lg:grid-cols-2 gap-16 xl:gap-28 items-start">
            <RevealWrapper direction="left" duration={700}>
              <div>
                <div className="flex items-center gap-3 mb-8">
                  <span className="font-mono text-xs px-2.5 py-1 border border-red-800/40 text-red-500/70 tracking-widest line-through">{t('games.status.cancelled')}</span>
                  <span className="font-mono text-xs text-neutral-500 tracking-widest">{t('games.am.category')}</span>
                </div>

                <div className="flex items-center gap-5 mb-8">
                  <div className="w-20 h-20 border border-red-800/25 flex items-center justify-center bg-[#1f1414] shrink-0 grayscale">
                    <img
                      src={gSoftLogoDark}
                      alt={t('am.name')}
                      className="w-16 h-16 object-contain opacity-50"
                    />
                  </div>
                  <h1 className="font-black text-neutral-500 text-5xl lg:text-6xl tracking-tight leading-none line-through decoration-red-700/40">{t('am.name.line1')}<br />{t('am.name.line2')}</h1>
                </div>

                <p className="text-neutral-400 text-base leading-loose max-w-md mb-6">{t('am.description')}</p>

                <div className="flex flex-wrap gap-2 mb-10">
                  {TAGS.map((tagKey) => (
                    <span key={tagKey} className="font-mono text-xs text-red-500/50 border border-red-800/30 px-3 py-1.5">
                      {t(tagKey)}
                    </span>
                  ))}
                </div>

                <div className="mt-6 mb-2">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-mono text-xs text-neutral-500 tracking-widest uppercase">{t('am.progress.label')}</span>
                    <span className="font-mono text-xs text-red-500/50 tracking-widest uppercase">{t('games.status.cancelled')}</span>
                  </div>
                  <div className="relative h-1.5 bg-red-900/20 w-full overflow-hidden">
                    <div className="absolute left-0 top-0 h-full w-0 bg-red-700/40" />
                  </div>
                  <div className="flex items-center justify-between mt-2">
                    <span className="font-mono text-[10px] text-neutral-600 tracking-widest">v0.0.0-archived</span>
                    <span className="font-mono text-[10px] text-red-500/40 tracking-widest">{t('am.progress.stopped')}</span>
                  </div>
                </div>
              </div>
            </RevealWrapper>

            <RevealWrapper direction="right" delay={200} duration={700}>
              <div className="space-y-3">
                <p className="font-mono text-[10px] text-neutral-500 tracking-widest uppercase mb-5">{t('am.archive.statusLabel')}</p>
                {ARCHIVE_NOTES.map((item) => (
                  <div key={item.labelKey} className="border border-red-800/20 bg-[#1e1212] px-5 py-4 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 flex items-center justify-center">
                        <i className={`${item.icon} text-red-600/50 text-base`} />
                      </div>
                      <span className="font-mono text-xs text-neutral-400 tracking-wider">{t(item.labelKey)}</span>
                    </div>
                    <span className="font-mono text-xs text-red-400/60 font-black">{t(item.valueKey)}</span>
                  </div>
                ))}

                <div className="border border-red-800/15 bg-[#1c1010] px-5 py-4 mt-4">
                  <p className="font-mono text-[10px] text-red-600/40 tracking-widest uppercase mb-2">{t('am.archive.noteLabel')}</p>
                  <p className="font-mono text-xs text-neutral-500 leading-relaxed">{t('am.archive.note')}</p>
                </div>
              </div>
            </RevealWrapper>
          </div>
        </div>

        <div className="border-t border-red-800/15 px-6 lg:px-14 py-4 flex items-center justify-between">
          <span className="font-mono text-xs text-neutral-600">anilar-muzesi � v0.0.0-archived</span>
          <span className="font-mono text-xs text-red-600/30">{t('am.footer.right')}</span>
        </div>
      </section>

      <section className="border-t border-red-800/15 bg-[#161010]">
        <div className="px-6 lg:px-14 py-8 border-b border-red-800/15 flex items-center justify-between">
          <span className="font-mono text-xs text-red-600/40 tracking-widest uppercase">{t('am.notes.label')}</span>
          <span className="font-mono text-xs text-neutral-600">{CONCEPT_NOTES.length} {t('am.notes.unit')}</span>
        </div>
        <div className="grid md:grid-cols-3 border-t border-l border-red-700/[0.17]">
          {CONCEPT_NOTES.map((note, idx) => (
            <RevealWrapper key={note.titleKey} direction="up" delay={idx * 80} threshold={0.1} className="h-full border-r border-b border-red-700/[0.17]">
              <div className="bg-[#161010] p-10 lg:p-12 h-full">
                <div className="w-10 h-10 border border-red-800/25 flex items-center justify-center mb-6">
                  <i className="ri-file-text-line text-red-600/40 text-base" />
                </div>
                <h3 className="text-sm font-black text-neutral-300 mb-4 tracking-tight">{t(note.titleKey)}</h3>
                <p className="text-neutral-500 text-sm leading-loose">{t(note.contentKey)}</p>
              </div>
            </RevealWrapper>
          ))}
        </div>
      </section>

      <section className="border-t border-red-800/15 bg-[#181010]">
        <div className="px-6 lg:px-14 py-8 border-b border-red-800/15">
          <span className="font-mono text-xs text-red-600/40 tracking-widest uppercase">{t('am.timeline.label')}</span>
        </div>
        <div className="px-6 lg:px-14 py-12 lg:py-16">
          <div className="space-y-0">
            {TIMELINE.map((item, idx) => (
              <RevealWrapper key={`${item.date}-${idx}`} direction="up" delay={idx * 80} threshold={0.1}>
                <div className={`flex gap-8 lg:gap-16 py-8 border-b border-red-800/10 last:border-0 ${item.status === 'cancelled' ? 'opacity-100' : 'opacity-50'}`}>
                  <div className="shrink-0 w-16">
                    <p className="font-mono text-xs text-neutral-500 tracking-widest">{item.date}</p>
                  </div>
                  <div className="flex-1">
                    <h3 className={`font-black text-lg mb-2 tracking-tight ${item.status === 'cancelled' ? 'text-red-500/60 line-through' : 'text-neutral-400'}`}>
                      {t(item.labelKey)}
                    </h3>
                    <p className="text-neutral-500 text-sm leading-relaxed">{t(item.noteKey)}</p>
                  </div>
                  <div className="shrink-0 w-6 h-6 border flex items-center justify-center mt-1 border-red-800/25">
                    {item.status === 'cancelled' ? (
                      <i className="ri-close-line text-red-500/50 text-xs" />
                    ) : (
                      <div className="w-2 h-2 bg-neutral-600 rounded-full" />
                    )}
                  </div>
                </div>
              </RevealWrapper>
            ))}
          </div>
        </div>
      </section>

      <RevealWrapper direction="up" threshold={0.1}>
        <div className="px-6 lg:px-14 py-16 border-t border-red-800/15 bg-[#161010] flex flex-col sm:flex-row items-center justify-between gap-6">
          <div>
            <p className="font-mono text-xs text-red-600/30 tracking-widest uppercase mb-2">{t('am.cta.label')}</p>
            <p className="text-neutral-400 text-sm">{t('am.cta.description')}</p>
          </div>
          <button
            onClick={() => navigate('/games')}
            className="group/btn inline-flex items-center gap-3 px-6 py-4 border border-red-800/35 text-red-500/60 font-mono text-xs tracking-widest uppercase hover:border-red-600/50 hover:text-red-400 transition-all duration-300 sm:whitespace-nowrap cursor-pointer"
          >
            {t('am.cta.button')}
            <i className="ri-arrow-right-line text-base group-hover/btn:translate-x-1 transition-transform duration-200" />
          </button>
        </div>
      </RevealWrapper>

      <SiteFooter />
      <ScrollToTop />
    </div>
  );
}

